import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, ChevronRight, ChevronLeft, Camera, User, FileText, Target, Sliders, ClipboardList, Loader2, Check, Sparkles, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { Slider } from '../components/ui/slider';

import API_URL from '../config/api';

const COUNTRIES = [
  { code: 'PT', name: 'Portugal', flag: '\u{1F1F5}\u{1F1F9}' },
  { code: 'FR', name: 'France', flag: '\u{1F1EB}\u{1F1F7}' },
  { code: 'CH', name: 'Switzerland', flag: '\u{1F1E8}\u{1F1ED}' },
  { code: 'LU', name: 'Luxembourg', flag: '\u{1F1F1}\u{1F1FA}' },
  { code: 'BE', name: 'Belgium', flag: '\u{1F1E7}\u{1F1EA}' },
  { code: 'DE', name: 'Germany', flag: '\u{1F1E9}\u{1F1EA}' },
  { code: 'ES', name: 'Spain', flag: '\u{1F1EA}\u{1F1F8}' },
];

const INTENTIONS = [
  { value: 'serious', label: 'Serious Relationship', desc: 'Looking for something long-term', icon: '\u{1F48D}' },
  { value: 'casual', label: 'Taking it Slow', desc: 'Open to seeing where it goes', icon: '\u{1F331}' },
  { value: 'friendship', label: 'Friendship First', desc: 'Let\'s start as friends', icon: '\u{1F91D}' },
  { value: 'not_sure', label: 'Not Sure Yet', desc: 'Just exploring for now', icon: '\u{1F914}' },
];

const TOTAL_STEPS = 6;

const STEP_META = [
  { title: 'Add Your Photos', subtitle: 'Show your best self', icon: Camera, encouragement: 'Great start!' },
  { title: 'About You', subtitle: 'The basics', icon: User, encouragement: 'Looking good!' },
  { title: 'Your Bio', subtitle: 'Tell people what makes you, you', icon: FileText, encouragement: 'Nice one!' },
  { title: 'What Are You Looking For?', subtitle: 'Set your intentions', icon: Target, encouragement: 'Almost there!' },
  { title: 'Your Preferences', subtitle: 'Who would you like to meet?', icon: Sliders, encouragement: "You're almost ready!" },
  { title: 'More About You', subtitle: 'Complete your profile to get better matches', icon: ClipboardList, encouragement: 'Final touch!' },
];

export default function Onboarding() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);

  const [formData, setFormData] = useState({
    nickname: user?.name || '',
    birthdate: null,
    gender: '',
    country: '',
    city: '',
    bio: '',
    looking_for: '',
    relationship_intention: '',
    preferred_age_min: 18,
    preferred_age_max: 45,
    preferred_countries: [],
    religion: '',
    height: '',
    smoking: '',
    drinking: '',
    has_children: '',
    wants_children: '',
    occupation: '',
    education: '',
  });

  const [photos, setPhotos] = useState([]);

  const currentMeta = STEP_META[step - 1];
  const StepIcon = currentMeta.icon;

  const updateForm = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const togglePreferredCountry = (code) => {
    setFormData(prev => ({
      ...prev,
      preferred_countries: prev.preferred_countries.includes(code)
        ? prev.preferred_countries.filter(c => c !== code)
        : [...prev.preferred_countries, code],
    }));
  };

  // ── Photo handlers ──
  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (photos.length + files.length > 6) {
      toast.error('Maximum 6 photos allowed');
      return;
    }
    setPhotoUploading(true);
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        const response = await axios.post(`${API_URL}/api/photos/upload`, fd, { withCredentials: true });
        setPhotos(prev => [...prev, { id: response.data.photo_id, preview: URL.createObjectURL(file) }]);
        toast.success('Photo uploaded');
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to upload photo');
      }
    }
    setPhotoUploading(false);
  };

  const removePhoto = async (photoId) => {
    try {
      await axios.delete(`${API_URL}/api/photos/${photoId}`, { withCredentials: true });
      setPhotos(prev => prev.filter(p => p.id !== photoId));
      toast.success('Photo removed');
    } catch {
      toast.error('Failed to remove photo');
    }
  };

  // ── Validation per step ──
  const canProceed = () => {
    switch (step) {
      case 1: return photos.length >= 1;
      case 2: return formData.nickname && formData.birthdate && formData.gender && formData.country && formData.city;
      case 3: return true; // bio is optional
      case 4: return formData.looking_for && formData.relationship_intention;
      case 5: return true; // preferences optional
      case 6: return true; // all optional
      default: return false;
    }
  };

  const handleNext = () => {
    if (step < TOTAL_STEPS) {
      setStep(step + 1);
      toast.success(currentMeta.encouragement, { icon: <Check className="w-4 h-4 text-[#2D6A4F]" /> });
    }
  };
  const handleBack = () => { if (step > 1) setStep(step - 1); };

  const handleComplete = async () => {
    setLoading(true);
    try {
      const profileData = {
        ...formData,
        birthdate: formData.birthdate ? format(formData.birthdate, 'yyyy-MM-dd') : null,
      };
      // Remove empty optional fields
      Object.keys(profileData).forEach(key => {
        if (profileData[key] === '' || profileData[key] === null) delete profileData[key];
      });
      // Only include preferred_countries if user chose any
      if (profileData.preferred_countries?.length === 0) delete profileData.preferred_countries;

      await axios.put(`${API_URL}/api/profile`, profileData, { withCredentials: true });

      if (photos.length > 0) {
        await axios.post(`${API_URL}/api/photos/${photos[0].id}/set-primary`, {}, { withCredentials: true });
      }

      await axios.post(`${API_URL}/api/profile/complete-onboarding`, {}, { withCredentials: true });
      await refreshUser();
      toast.success('Your profile is live!', { icon: <Sparkles className="w-4 h-4 text-[#6E1A24]" /> });
      navigate('/discover');
    } catch {
      toast.error('Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  const getAge = (date) => {
    if (!date) return null;
    const today = new Date();
    let age = today.getFullYear() - date.getFullYear();
    const m = today.getMonth() - date.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < date.getDate())) age--;
    return age;
  };

  // ── Progress bar (smooth) ──
  const progressPercent = ((step - 1) / (TOTAL_STEPS - 1)) * 100;

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-[#FAF9F6]/95 backdrop-blur-sm border-b border-[#E5E3DB] h-16 flex items-center px-6">
        <div className="flex items-center gap-2">
          <Heart className="w-6 h-6 text-[#6E1A24]" strokeWidth={1.5} fill="currentColor" fillOpacity={0.1} />
          <span className="text-xl font-serif font-medium text-[#2D2D2A]">LusoLove</span>
        </div>
      </header>

      <div className="pt-24 pb-36 px-6">
        <div className="max-w-lg mx-auto">
          {/* Progress Bar */}
          <div className="mb-8" data-testid="onboarding-progress">
            <div className="h-2 bg-[#E5E3DB] rounded-full overflow-hidden">
              <div
                className="h-full bg-[#6E1A24] rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <div className="flex items-center justify-between mt-3">
              <p className="text-sm text-[#6C6C67]">Step {step} of {TOTAL_STEPS}</p>
              <p className="text-sm text-[#6C6C67]">
                {step === TOTAL_STEPS ? 'Final step' : `${TOTAL_STEPS - step} step${TOTAL_STEPS - step > 1 ? 's' : ''} left`}
              </p>
            </div>
          </div>

          {/* Step Header */}
          <div className="flex items-center gap-4 mb-8">
            <div className="w-14 h-14 rounded-2xl bg-[#6E1A24]/10 flex items-center justify-center">
              <StepIcon className="w-7 h-7 text-[#6E1A24]" />
            </div>
            <div>
              <h2 className="text-2xl font-serif font-medium text-[#2D2D2A]">{currentMeta.title}</h2>
              <p className="text-[#6C6C67]">{currentMeta.subtitle}</p>
            </div>
          </div>

          {/* ─── STEP 1: Photos (mandatory) ─── */}
          {step === 1 && (
            <div className="animate-fade-in space-y-6">
              <div className="bg-[#2D6A4F]/10 border border-[#2D6A4F]/20 rounded-2xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#2D6A4F] flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-medium text-[#2D6A4F]">Profiles with photos get 9x more likes</p>
                  <p className="text-sm text-[#2D6A4F]/80">Upload at least 1 photo to continue</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                {photos.map((photo, i) => (
                  <div key={photo.id} className="relative aspect-[3/4] rounded-2xl overflow-hidden bg-[#F5F2EB] shadow-sm">
                    <img src={photo.preview} alt="" className="w-full h-full object-cover" />
                    <button
                      onClick={() => removePhoto(photo.id)}
                      className="absolute top-2 right-2 w-7 h-7 bg-black/60 rounded-full flex items-center justify-center text-white text-sm hover:bg-black/80 transition-colors"
                      data-testid={`remove-photo-${i}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                    {i === 0 && (
                      <div className="absolute bottom-2 left-2 bg-[#6E1A24] text-white text-xs px-3 py-1 rounded-full font-medium">
                        Main
                      </div>
                    )}
                  </div>
                ))}

                {photos.length < 6 && (
                  <label className="aspect-[3/4] rounded-2xl border-2 border-dashed border-[#E5E3DB] flex flex-col items-center justify-center cursor-pointer hover:border-[#6E1A24] hover:bg-[#6E1A24]/5 transition-all" data-testid="photo-upload">
                    {photoUploading ? (
                      <Loader2 className="w-8 h-8 text-[#6E1A24] animate-spin" />
                    ) : (
                      <>
                        <Camera className="w-8 h-8 text-[#6C6C67] mb-2" />
                        <span className="text-sm text-[#6C6C67] font-medium">Add Photo</span>
                      </>
                    )}
                    <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handlePhotoUpload} className="hidden" multiple disabled={photoUploading} />
                  </label>
                )}
              </div>

              <div className="bg-[#F5F2EB] rounded-2xl p-5">
                <p className="font-medium text-[#2D2D2A] mb-3">Photo tips</p>
                <ul className="space-y-2 text-sm text-[#6C6C67]">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#2D6A4F]" />Use a clear face photo as your first</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#2D6A4F]" />Show your smile — it works!</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#2D6A4F]" />Add a full-body shot and a fun activity pic</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#2D6A4F]" />Avoid group photos as your main</li>
                </ul>
              </div>
            </div>
          )}

          {/* ─── STEP 2: Basic Info ─── */}
          {step === 2 && (
            <div className="animate-fade-in space-y-6">
              <div>
                <label className="text-sm font-medium text-[#2D2D2A] block mb-2">First Name</label>
                <input
                  type="text"
                  value={formData.nickname}
                  onChange={(e) => updateForm('nickname', e.target.value)}
                  className="w-full px-4 py-4 bg-white border border-[#E5E3DB] rounded-2xl focus:outline-none focus:border-[#6E1A24] focus:ring-4 focus:ring-[#6E1A24]/5 transition-all text-lg"
                  placeholder="Your first name"
                  data-testid="onboarding-nickname"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-[#2D2D2A] block mb-2">Date of Birth</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      className="w-full text-left px-4 py-4 bg-white border border-[#E5E3DB] rounded-2xl hover:border-[#6E1A24]/50 focus:outline-none focus:border-[#6E1A24] focus:ring-4 focus:ring-[#6E1A24]/5 transition-all"
                      data-testid="onboarding-birthdate"
                    >
                      {formData.birthdate ? (
                        <span className="text-[#2D2D2A] text-lg">
                          {format(formData.birthdate, 'MMMM d, yyyy')}
                          {getAge(formData.birthdate) && (
                            <span className="text-[#6C6C67] ml-2 text-base">({getAge(formData.birthdate)} years)</span>
                          )}
                        </span>
                      ) : (
                        <span className="text-[#6C6C67] text-lg">Select your birthday</span>
                      )}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.birthdate}
                      onSelect={(date) => updateForm('birthdate', date)}
                      disabled={(date) => {
                        const today = new Date();
                        const minAge = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
                        return date > minAge || date < new Date('1940-01-01');
                      }}
                      initialFocus
                      captionLayout="dropdown-buttons"
                      fromYear={1940}
                      toYear={new Date().getFullYear() - 18}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <label className="text-sm font-medium text-[#2D2D2A] block mb-2">I am a...</label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'male', label: 'Man' },
                    { value: 'female', label: 'Woman' },
                    { value: 'non_binary', label: 'Non-binary' },
                  ].map(option => (
                    <button
                      key={option.value}
                      onClick={() => updateForm('gender', option.value)}
                      className={`py-4 rounded-2xl border-2 font-medium transition-all ${
                        formData.gender === option.value
                          ? 'border-[#6E1A24] bg-[#6E1A24]/5 text-[#6E1A24]'
                          : 'border-[#E5E3DB] hover:border-[#6E1A24]/50 text-[#2D2D2A]'
                      }`}
                      data-testid={`gender-${option.value}`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-[#2D2D2A] block mb-2">Country</label>
                <div className="grid grid-cols-2 gap-3">
                  {COUNTRIES.map(country => (
                    <button
                      key={country.code}
                      onClick={() => updateForm('country', country.code)}
                      className={`py-3 px-4 rounded-2xl border-2 text-left transition-all flex items-center gap-3 ${
                        formData.country === country.code
                          ? 'border-[#6E1A24] bg-[#6E1A24]/5'
                          : 'border-[#E5E3DB] hover:border-[#6E1A24]/50'
                      }`}
                      data-testid={`country-${country.code}`}
                    >
                      <span className="text-xl">{country.flag}</span>
                      <span className={`font-medium text-sm ${formData.country === country.code ? 'text-[#6E1A24]' : 'text-[#2D2D2A]'}`}>{country.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-[#2D2D2A] block mb-2">City</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => updateForm('city', e.target.value)}
                  className="w-full px-4 py-4 bg-white border border-[#E5E3DB] rounded-2xl focus:outline-none focus:border-[#6E1A24] focus:ring-4 focus:ring-[#6E1A24]/5 transition-all text-lg"
                  placeholder="e.g. Paris, Lisbon, Zurich..."
                  data-testid="onboarding-city"
                />
              </div>
            </div>
          )}

          {/* ─── STEP 3: Bio ─── */}
          {step === 3 && (
            <div className="animate-fade-in space-y-6">
              <textarea
                value={formData.bio}
                onChange={(e) => updateForm('bio', e.target.value)}
                className="w-full p-4 bg-white border border-[#E5E3DB] rounded-2xl focus:outline-none focus:border-[#6E1A24] focus:ring-4 focus:ring-[#6E1A24]/5 transition-all resize-none h-40 text-base"
                placeholder="What makes you unique? Share a bit about yourself, your passions, or what you're looking for..."
                data-testid="onboarding-bio"
              />
              <div className="flex justify-between">
                <p className="text-sm text-[#6C6C67]">Tip: Mention something conversation-worthy</p>
                <p className="text-sm text-[#6C6C67]">{formData.bio.length > 0 ? `${formData.bio.length} chars` : 'Optional'}</p>
              </div>

              <div className="bg-[#F5F2EB] rounded-2xl p-5">
                <p className="font-medium text-[#2D2D2A] mb-3">What works well</p>
                <ul className="space-y-2 text-sm text-[#6C6C67]">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#2D6A4F]" />Share a hobby or passion</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#2D6A4F]" />Mention what makes you laugh</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#2D6A4F]" />Say what your ideal weekend looks like</li>
                </ul>
              </div>
            </div>
          )}

          {/* ─── STEP 4: Intentions ─── */}
          {step === 4 && (
            <div className="animate-fade-in space-y-8">
              <div>
                <label className="text-sm font-medium text-[#2D2D2A] block mb-3">Interested in</label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'male', label: 'Men' },
                    { value: 'female', label: 'Women' },
                    { value: 'everyone', label: 'Everyone' },
                  ].map(option => (
                    <button
                      key={option.value}
                      onClick={() => updateForm('looking_for', option.value)}
                      className={`py-4 rounded-2xl border-2 font-medium transition-all ${
                        formData.looking_for === option.value
                          ? 'border-[#6E1A24] bg-[#6E1A24]/5 text-[#6E1A24]'
                          : 'border-[#E5E3DB] hover:border-[#6E1A24]/50 text-[#2D2D2A]'
                      }`}
                      data-testid={`looking-${option.value}`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-[#2D2D2A] block mb-3">What are you looking for?</label>
                <div className="space-y-3">
                  {INTENTIONS.map(intention => (
                    <button
                      key={intention.value}
                      onClick={() => updateForm('relationship_intention', intention.value)}
                      className={`w-full p-5 rounded-2xl border-2 text-left transition-all flex items-center gap-4 ${
                        formData.relationship_intention === intention.value
                          ? 'border-[#6E1A24] bg-[#6E1A24]/5'
                          : 'border-[#E5E3DB] hover:border-[#6E1A24]/50'
                      }`}
                      data-testid={`intention-${intention.value}`}
                    >
                      <span className="text-2xl">{intention.icon}</span>
                      <div>
                        <p className={`font-medium ${formData.relationship_intention === intention.value ? 'text-[#6E1A24]' : 'text-[#2D2D2A]'}`}>
                          {intention.label}
                        </p>
                        <p className="text-sm text-[#6C6C67]">{intention.desc}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ─── STEP 5: Preferences ─── */}
          {step === 5 && (
            <div className="animate-fade-in space-y-8">
              <div>
                <label className="text-sm font-medium text-[#2D2D2A] block mb-3">
                  Preferred Age Range
                </label>
                <div className="px-2">
                  <Slider
                    defaultValue={[formData.preferred_age_min, formData.preferred_age_max]}
                    min={18}
                    max={70}
                    step={1}
                    onValueChange={([min, max]) => {
                      updateForm('preferred_age_min', min);
                      updateForm('preferred_age_max', max);
                    }}
                    className="mb-2"
                  />
                  <div className="flex justify-between text-sm text-[#6C6C67]">
                    <span>{formData.preferred_age_min} years</span>
                    <span>{formData.preferred_age_max}+ years</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-[#2D2D2A] block mb-3">
                  Preferred Countries / Regions
                </label>
                <p className="text-sm text-[#6C6C67] mb-4">Select all that apply, or skip to see everyone</p>
                <div className="grid grid-cols-2 gap-3">
                  {COUNTRIES.map(country => (
                    <button
                      key={country.code}
                      onClick={() => togglePreferredCountry(country.code)}
                      className={`py-3 px-4 rounded-2xl border-2 text-left transition-all flex items-center gap-3 ${
                        formData.preferred_countries.includes(country.code)
                          ? 'border-[#6E1A24] bg-[#6E1A24]/5'
                          : 'border-[#E5E3DB] hover:border-[#6E1A24]/50'
                      }`}
                      data-testid={`pref-country-${country.code}`}
                    >
                      <span className="text-xl">{country.flag}</span>
                      <span className={`font-medium text-sm ${formData.preferred_countries.includes(country.code) ? 'text-[#6E1A24]' : 'text-[#2D2D2A]'}`}>
                        {country.name}
                      </span>
                      {formData.preferred_countries.includes(country.code) && (
                        <Check className="w-4 h-4 text-[#6E1A24] ml-auto" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ─── STEP 6: Extended Fields (all optional) ─── */}
          {step === 6 && (
            <div className="animate-fade-in space-y-5">
              <div className="bg-[#6E1A24]/5 border border-[#6E1A24]/10 rounded-2xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#6E1A24]/10 flex items-center justify-center">
                  <ClipboardList className="w-5 h-5 text-[#6E1A24]" />
                </div>
                <div>
                  <p className="font-medium text-[#6E1A24]">All fields are optional</p>
                  <p className="text-sm text-[#6E1A24]/70">Fill what you'd like — skip the rest. You can always edit later.</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-[#2D2D2A] mb-1.5">Occupation</label>
                  <input type="text" placeholder="e.g. Software Engineer" value={formData.occupation} onChange={(e) => updateForm('occupation', e.target.value)} className="w-full px-4 py-3 bg-white border border-[#E5E3DB] rounded-xl text-[#2D2D2A] placeholder-[#9C9C97] focus:outline-none focus:border-[#6E1A24] focus:ring-1 focus:ring-[#6E1A24] transition-all" data-testid="onboarding-occupation" />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-[#2D2D2A] mb-1.5">Education</label>
                  <select value={formData.education} onChange={(e) => updateForm('education', e.target.value)} className="w-full px-4 py-3 bg-white border border-[#E5E3DB] rounded-xl text-[#2D2D2A] focus:outline-none focus:border-[#6E1A24] focus:ring-1 focus:ring-[#6E1A24] transition-all appearance-none" data-testid="onboarding-education">
                    <option value="">Select...</option>
                    <option value="high_school">High School</option>
                    <option value="bachelors">Bachelor's Degree</option>
                    <option value="masters">Master's Degree</option>
                    <option value="doctorate">Doctorate / PhD</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2D2D2A] mb-1.5">Height (cm)</label>
                  <input type="number" placeholder="e.g. 175" min="100" max="250" value={formData.height} onChange={(e) => updateForm('height', e.target.value ? parseInt(e.target.value) : '')} className="w-full px-4 py-3 bg-white border border-[#E5E3DB] rounded-xl text-[#2D2D2A] placeholder-[#9C9C97] focus:outline-none focus:border-[#6E1A24] focus:ring-1 focus:ring-[#6E1A24] transition-all" data-testid="onboarding-height" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2D2D2A] mb-1.5">Religion</label>
                  <select value={formData.religion} onChange={(e) => updateForm('religion', e.target.value)} className="w-full px-4 py-3 bg-white border border-[#E5E3DB] rounded-xl text-[#2D2D2A] focus:outline-none focus:border-[#6E1A24] focus:ring-1 focus:ring-[#6E1A24] transition-all appearance-none" data-testid="onboarding-religion">
                    <option value="">Select...</option>
                    <option value="catholic">Catholic</option>
                    <option value="christian">Christian</option>
                    <option value="muslim">Muslim</option>
                    <option value="jewish">Jewish</option>
                    <option value="buddhist">Buddhist</option>
                    <option value="agnostic">Agnostic</option>
                    <option value="atheist">Atheist</option>
                    <option value="spiritual">Spiritual</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2D2D2A] mb-1.5">Smoking</label>
                  <div className="flex gap-2">
                    {['yes', 'no'].map(val => (
                      <button key={val} onClick={() => updateForm('smoking', formData.smoking === val ? '' : val)} className={`flex-1 py-3 rounded-xl border text-sm font-medium transition-all ${formData.smoking === val ? 'bg-[#6E1A24] text-white border-[#6E1A24]' : 'bg-white text-[#6C6C67] border-[#E5E3DB] hover:border-[#6E1A24]/30'}`} data-testid={`onboarding-smoking-${val}`}>{val === 'yes' ? 'Yes' : 'No'}</button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2D2D2A] mb-1.5">Drinking</label>
                  <div className="flex gap-2">
                    {['yes', 'no'].map(val => (
                      <button key={val} onClick={() => updateForm('drinking', formData.drinking === val ? '' : val)} className={`flex-1 py-3 rounded-xl border text-sm font-medium transition-all ${formData.drinking === val ? 'bg-[#6E1A24] text-white border-[#6E1A24]' : 'bg-white text-[#6C6C67] border-[#E5E3DB] hover:border-[#6E1A24]/30'}`} data-testid={`onboarding-drinking-${val}`}>{val === 'yes' ? 'Yes' : 'No'}</button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2D2D2A] mb-1.5">Has children</label>
                  <div className="flex gap-2">
                    {['yes', 'no'].map(val => (
                      <button key={val} onClick={() => updateForm('has_children', formData.has_children === val ? '' : val)} className={`flex-1 py-3 rounded-xl border text-sm font-medium transition-all ${formData.has_children === val ? 'bg-[#6E1A24] text-white border-[#6E1A24]' : 'bg-white text-[#6C6C67] border-[#E5E3DB] hover:border-[#6E1A24]/30'}`} data-testid={`onboarding-has-children-${val}`}>{val === 'yes' ? 'Yes' : 'No'}</button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2D2D2A] mb-1.5">Wants children</label>
                  <div className="flex gap-2">
                    {['yes', 'no'].map(val => (
                      <button key={val} onClick={() => updateForm('wants_children', formData.wants_children === val ? '' : val)} className={`flex-1 py-3 rounded-xl border text-sm font-medium transition-all ${formData.wants_children === val ? 'bg-[#6E1A24] text-white border-[#6E1A24]' : 'bg-white text-[#6C6C67] border-[#E5E3DB] hover:border-[#6E1A24]/30'}`} data-testid={`onboarding-wants-children-${val}`}>{val === 'yes' ? 'Yes' : 'No'}</button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E5E3DB] p-4 pb-safe">
        <div className="max-w-lg mx-auto flex gap-4">
          {step > 1 && (
            <Button
              onClick={handleBack}
              className="bg-[#F5F2EB] text-[#2D2D2A] rounded-2xl px-6 py-4 font-medium hover:bg-[#E5E3DB] transition-all flex items-center gap-2"
              data-testid="onboarding-back"
            >
              <ChevronLeft className="w-5 h-5" />
              Back
            </Button>
          )}

          {step < TOTAL_STEPS ? (
            <Button
              onClick={handleNext}
              disabled={!canProceed()}
              className="bg-[#6E1A24] text-white rounded-2xl flex-1 py-4 font-semibold hover:bg-[#8A2331] hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="onboarding-next"
            >
              Continue
              <ChevronRight className="w-5 h-5" />
            </Button>
          ) : (
            <div className="flex gap-3 flex-1">
              <Button
                onClick={handleComplete}
                variant="outline"
                disabled={loading}
                className="rounded-2xl py-4 font-semibold border-[#E5E3DB] text-[#6C6C67] hover:bg-[#F5F4F0] transition-all flex items-center justify-center gap-2 px-6"
                data-testid="onboarding-skip"
              >
                Skip
              </Button>
              <Button
                onClick={handleComplete}
                disabled={loading}
                className="bg-[#6E1A24] text-white rounded-2xl flex-1 py-4 font-semibold hover:bg-[#8A2331] hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="onboarding-complete"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Creating profile...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Complete Profile
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
